import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';
import LoadingSpinner from './LoadingSpinner';

const CUALtransporteComponent = ({ user }) => {
  const [tab, setTab] = useState('explorar'); // explorar | mias | nueva
  const [classes, setClasses] = useState([]);
  const [requests, setRequests] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterClass, setFilterClass] = useState('');
  const [selected, setSelected] = useState(null); // request_id selected for details
  const [details, setDetails] = useState(null); // {request, offers}
  const [offerForm, setOfferForm] = useState({ price: '', eta: '', message: '' });

  const [form, setForm] = useState({
    vehicle_class: 'camion_350',
    cargo_description: '',
    cargo_weight_kg: '',
    pickup_address: '',
    dropoff_address: '',
    budget_cualcoin: '',
    notes: '',
  });

  const fetchClasses = useCallback(async () => {
    const r = await axios.get(`${API}/cualtransporte/vehicle-classes`);
    setClasses(r.data.classes || []);
  }, []);

  const fetchRequests = useCallback(async () => {
    const params = new URLSearchParams();
    if (filterClass) params.set('vehicle_class', filterClass);
    const r = await axios.get(`${API}/cualtransporte/requests?${params}`);
    setRequests(r.data.requests || []);
  }, [filterClass]);

  const fetchMyRequests = useCallback(async () => {
    const r = await axios.get(`${API}/cualtransporte/requests?mine=true&limit=100`);
    setMyRequests(r.data.requests || []);
  }, []);

  useEffect(() => {
    (async () => {
      await Promise.all([fetchClasses(), fetchRequests(), fetchMyRequests()]);
      setLoading(false);
    })();
  }, [fetchClasses, fetchRequests, fetchMyRequests]);

  useEffect(() => { fetchRequests(); }, [filterClass, fetchRequests]);

  const submitNewRequest = async () => {
    if (!form.cargo_description || !form.pickup_address || !form.dropoff_address) {
      alert('Completa descripcion, origen y destino');
      return;
    }
    try {
      await axios.post(`${API}/cualtransporte/requests`, {
        ...form,
        cargo_weight_kg: form.cargo_weight_kg ? parseFloat(form.cargo_weight_kg) : null,
        budget_cualcoin: form.budget_cualcoin ? parseFloat(form.budget_cualcoin) : null,
      });
      setForm({ vehicle_class: 'camion_350', cargo_description: '', cargo_weight_kg: '', pickup_address: '', dropoff_address: '', budget_cualcoin: '', notes: '' });
      setTab('mias');
      await fetchMyRequests();
      await fetchRequests();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const openDetails = async (id) => {
    setSelected(id);
    try {
      const r = await axios.get(`${API}/cualtransporte/requests/${id}`);
      setDetails(r.data);
    } catch { setDetails(null); }
  };

  const submitOffer = async () => {
    if (!selected || !offerForm.price) return;
    try {
      await axios.post(`${API}/cualtransporte/offers`, {
        request_id: selected,
        price_cualcoin: parseFloat(offerForm.price),
        eta_hours: offerForm.eta ? parseFloat(offerForm.eta) : null,
        message: offerForm.message || null,
      });
      setOfferForm({ price: '', eta: '', message: '' });
      await openDetails(selected);
      await fetchRequests();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const acceptOffer = async (offerId) => {
    if (!window.confirm('¿Aceptar esta oferta?')) return;
    try {
      await axios.post(`${API}/cualtransporte/offers/${offerId}/accept`);
      await openDetails(selected);
      await fetchMyRequests();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  if (loading) return <LoadingSpinner />;

  const className = (id) => classes.find(c => c.id === id)?.name || id;

  return (
    <div className="space-y-4" data-testid="cualtransporte-component">
      <div className="bg-gradient-to-br from-blue-700 via-indigo-700 to-purple-700 rounded-2xl p-5 text-white shadow-lg">
        <h2 className="text-2xl font-bold flex items-center gap-2">🚚 CUALtransporte</h2>
        <p className="text-sm text-white/90 mt-1">Coordina fletes, mudanzas y carga: camiones, gandolas, aviones, embarcaciones.</p>
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        {[
          { id: 'explorar', label: 'Solicitudes abiertas' },
          { id: 'mias', label: 'Mis solicitudes' },
          { id: 'nueva', label: 'Nueva solicitud' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`ct-tab-${t.id}`}
            className={`px-3 py-2 text-sm font-medium border-b-2 ${tab === t.id ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
          >{t.label}</button>
        ))}
      </div>

      {/* EXPLORE */}
      {tab === 'explorar' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">Filtrar por clase:</label>
            <select value={filterClass} onChange={(e) => setFilterClass(e.target.value)} data-testid="ct-filter" className="px-3 py-1.5 border rounded text-sm">
              <option value="">Todas</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {requests.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">No hay solicitudes abiertas en este momento.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {requests.map(r => (
                <button key={r.id} onClick={() => openDetails(r.id)} data-testid={`ct-req-${r.id}`}
                  className="bg-white rounded-xl border border-gray-200 p-4 text-left hover:border-indigo-400 hover:shadow-md transition-all"
                >
                  <p className="font-semibold text-gray-800">{className(r.vehicle_class)}</p>
                  <p className="text-sm text-gray-700 mt-1">{r.cargo_description}</p>
                  <p className="text-xs text-gray-500 mt-2">📍 {r.pickup_address.substring(0, 40)}{r.pickup_address.length > 40 ? '...' : ''}</p>
                  <p className="text-xs text-gray-500">🎯 {r.dropoff_address.substring(0, 40)}{r.dropoff_address.length > 40 ? '...' : ''}</p>
                  {r.budget_cualcoin && <p className="text-amber-700 font-bold text-sm mt-2">Presupuesto: {r.budget_cualcoin.toFixed(2)} Cc</p>}
                  <p className="text-[10px] text-gray-400 mt-1">por {r.creator_name}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* MINE */}
      {tab === 'mias' && (
        <div className="space-y-2" data-testid="ct-mine-panel">
          {myRequests.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">No has creado solicitudes ni aceptado ofertas.</p>
          ) : myRequests.map(r => (
            <button key={r.id} onClick={() => openDetails(r.id)} className="w-full bg-white rounded-xl border p-3 text-left hover:border-indigo-400">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium">{className(r.vehicle_class)}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{r.cargo_description.substring(0, 60)}</p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  r.status === 'open' ? 'bg-blue-100 text-blue-700' :
                  r.status === 'offered' ? 'bg-amber-100 text-amber-700' :
                  r.status === 'accepted' ? 'bg-green-100 text-green-700' :
                  'bg-gray-100 text-gray-700'
                }`}>{r.status}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* NEW */}
      {tab === 'nueva' && (
        <div className="bg-white rounded-xl p-5 border border-gray-200 space-y-3" data-testid="ct-new-form">
          <h3 className="font-semibold text-gray-800">Nueva solicitud de carga</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="md:col-span-2">
              <label className="text-xs text-gray-500">Clase de vehiculo</label>
              <select value={form.vehicle_class} onChange={(e) => setForm({...form, vehicle_class: e.target.value})} data-testid="ct-class" className="w-full mt-0.5 px-3 py-2 border rounded">
                {classes.map(c => <option key={c.id} value={c.id}>{c.name} (hasta {c.capacity_kg} kg)</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-gray-500">Descripcion de la carga</label>
              <textarea value={form.cargo_description} onChange={(e) => setForm({...form, cargo_description: e.target.value})} data-testid="ct-cargo-desc" rows={2} className="w-full mt-0.5 px-3 py-2 border rounded" placeholder="Ej: Mudanza de apartamento 2 habitaciones, ~60 cajas + electrodomesticos" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Peso estimado (kg)</label>
              <input type="number" value={form.cargo_weight_kg} onChange={(e) => setForm({...form, cargo_weight_kg: e.target.value})} data-testid="ct-weight" className="w-full mt-0.5 px-3 py-2 border rounded" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Presupuesto (Cc, opcional)</label>
              <input type="number" step="0.01" value={form.budget_cualcoin} onChange={(e) => setForm({...form, budget_cualcoin: e.target.value})} data-testid="ct-budget" className="w-full mt-0.5 px-3 py-2 border rounded" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-gray-500">Direccion de origen</label>
              <input value={form.pickup_address} onChange={(e) => setForm({...form, pickup_address: e.target.value})} data-testid="ct-pickup" className="w-full mt-0.5 px-3 py-2 border rounded" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-gray-500">Direccion de destino</label>
              <input value={form.dropoff_address} onChange={(e) => setForm({...form, dropoff_address: e.target.value})} data-testid="ct-dropoff" className="w-full mt-0.5 px-3 py-2 border rounded" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-gray-500">Notas</label>
              <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={2} className="w-full mt-0.5 px-3 py-2 border rounded" />
            </div>
          </div>
          <div className="flex justify-end">
            <button onClick={submitNewRequest} data-testid="ct-submit" className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium">
              Publicar solicitud
            </button>
          </div>
        </div>
      )}

      {/* DETAILS MODAL */}
      {selected && details && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setSelected(null); setDetails(null); }}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="ct-details">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-bold text-lg">{className(details.request.vehicle_class)}</p>
                <p className="text-xs text-gray-500">por {details.request.creator_name} · {details.request.status}</p>
              </div>
              <button onClick={() => { setSelected(null); setDetails(null); }} className="text-gray-400 hover:text-gray-700">✕</button>
            </div>
            <p className="text-sm">{details.request.cargo_description}</p>
            <div className="text-xs text-gray-600 space-y-1">
              <p>📍 Origen: {details.request.pickup_address}</p>
              <p>🎯 Destino: {details.request.dropoff_address}</p>
              {details.request.cargo_weight_kg && <p>⚖️ Peso: {details.request.cargo_weight_kg} kg</p>}
              {details.request.budget_cualcoin && <p>💰 Presupuesto: {details.request.budget_cualcoin.toFixed(2)} Cc</p>}
              {details.request.notes && <p className="italic">{details.request.notes}</p>}
            </div>

            <div className="border-t pt-3">
              <p className="font-semibold text-sm mb-2">Ofertas ({details.offers.length})</p>
              {details.offers.length === 0 && <p className="text-xs text-gray-500">Aun sin ofertas</p>}
              {details.offers.map(o => (
                <div key={o.id} className="bg-gray-50 rounded p-2 text-sm flex justify-between items-center mt-1">
                  <div>
                    <p className="font-medium">{o.carrier_name}</p>
                    <p className="text-xs text-gray-500">{o.price_cualcoin.toFixed(2)} Cc{o.eta_hours ? ` · ETA ${o.eta_hours}h` : ''}</p>
                    {o.message && <p className="text-xs italic">{o.message}</p>}
                  </div>
                  {details.request.creator_id === user.id && details.request.status !== 'accepted' && (
                    <button onClick={() => acceptOffer(o.id)} data-testid={`ct-accept-${o.id}`} className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded">Aceptar</button>
                  )}
                </div>
              ))}
            </div>

            {/* Si NO soy el creador y la solicitud esta abierta, permitir ofertar */}
            {details.request.creator_id !== user.id && ['open', 'offered'].includes(details.request.status) && (
              <div className="border-t pt-3 space-y-2">
                <p className="font-semibold text-sm">Hacer oferta</p>
                <div className="grid grid-cols-2 gap-2">
                  <input type="number" step="0.01" value={offerForm.price} onChange={(e) => setOfferForm({...offerForm, price: e.target.value})} placeholder="Precio Cc" data-testid="ct-offer-price" className="px-2 py-1.5 border rounded text-sm" />
                  <input type="number" step="0.5" value={offerForm.eta} onChange={(e) => setOfferForm({...offerForm, eta: e.target.value})} placeholder="ETA horas" className="px-2 py-1.5 border rounded text-sm" />
                </div>
                <input value={offerForm.message} onChange={(e) => setOfferForm({...offerForm, message: e.target.value})} placeholder="Mensaje (opcional)" className="w-full px-2 py-1.5 border rounded text-sm" />
                <button onClick={submitOffer} data-testid="ct-offer-submit" className="w-full px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-sm">Enviar oferta</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CUALtransporteComponent;
